<template>
  <section
    v-if="widget && widget.available"
    class="widget grid-stack-item-content"
    :class="{ 'widget-edit': editMode, 'premium-metric': widget.is_premium }"
  >
    <div class="widget__header">
      <IconBar />
      <div class="widget__header-title">
        <span>{{ widget.name }}</span>
        <Tooltip v-if="widget.is_premium" position="top">
          <template v-slot:text>
            <IconPremium class="premium-icon" />
          </template>
          <template v-slot:tooltip> Premium Feature </template>
        </Tooltip>
      </div>
      <Tooltip position="left">
        <template v-slot:text>
          <IconInfo />
        </template>
        <template v-slot:tooltip>
          <span>
            {{ widget.description }}
          </span>
        </template>
      </Tooltip>
      <ul class="widget__header-controls">
        <li
          v-for="(area, index) in widget.filters.area"
          :key="index"
          @click="changeArea(area)"
          class="widget__header-control"
          :class="{
            'widget__header-control--active': chosenArea?.name === area.name,
            'widget__header-control--solo': widget.filters.area.length === 1,
          }"
        >
          {{ area.name }}
        </li>
      </ul>
    </div>

    <FormRow class="widget__subheader">
      <FormCell
        v-if="showZoneSelection"
        class="widget__subheader-controls widget__subheader-controls__zones"
      >
        <div class="widget__label">Zone</div>
        <InputSelect
          v-if="chosenArea?.data"
          v-model="chosenZone"
          :options="chosenArea?.data"
          optionLabel="title"
          optionValue="id"
          @optionSelected="changeZone"
        />
      </FormCell>
      <FormCell
        v-if="showClusterSelection"
        class="widget__subheader-controls widget__subheader-controls__zones"
      >
        <div class="widget__label">Cluster</div>
        <InputSelect
          v-if="chosenArea?.data"
          v-model="chosenCluster"
          :options="chosenArea?.data"
          optionLabel="title"
          optionValue="id"
          @optionSelected="changeCluster"
        />
      </FormCell>
      <ul class="widget__subheader-controls widget__subheader-controls__days">
        <li
          v-for="(time, index) in widget.filters.time"
          :key="index"
          @click="timeIndex = index"
          class="widget__subheader-control"
          :class="{ 'widget__header-control--active': chosenTime?.name === time.name }"
        >
          {{ time.name }}
        </li>
      </ul>
      <FormCell
        v-if="showDaySelection"
        class="widget__subheader-controls widget__subheader-controls__zones"
      >
        <div class="widget__label">Day</div>
        <InputSelect
          v-model="chosenDay"
          :options="chosenTime?.data"
          optionLabel="title"
          optionValue="id"
          @optionSelected="changeDay"
        />
      </FormCell>

    </FormRow>

    <div class="widget__body-wrapper">
      <div class="widget__body">
        <div v-if="widget.data.length === 0 && !widget.is_premium" class="widget__body-empty">
          <IconInfo />
          <p>No data available</p>
        </div>
        <!-- Average Visits -->
        <!-- Average Repeats -->
        <AveragesDisplay
          v-else-if="widget.code_name === 'AverageVisits' || widget.code_name === 'AverageRepeats'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :cluster="chosenCluster"
        />

        <!-- Unique Visitors -->
        <UniqueVisitors
          v-else-if="widget.code_name === 'UniqueVisitors'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :cluster="chosenCluster"
          :day="chosenDay"
        />

        <!-- Zones Visited -->
        <ZonesVisited
          v-else-if="widget.code_name === 'ZonesVisited'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :cluster="chosenCluster"
        />

        <!-- Days Visited -->
        <DaysVisited v-else-if="widget.code_name === 'DaysVisited'" :widget="widget" />

        <!-- Visits -->
        <Visits
          v-else-if="widget.code_name === 'Visits'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :cluster="chosenCluster"
        />

        <!-- Repeat Visits -->
        <RepeatVisits
          v-else-if="widget.code_name === 'RepeatVisits'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :cluster="chosenCluster"
        />

        <RepeatVisitors
          v-else-if="widget.code_name === 'RepeatVisitors'"
          :widget="widget"
          :area="chosenArea"
          :cluster="chosenCluster"
        />

        <!-- Visit Dwell -->
        <VisitorDwell
          v-else-if="widget.code_name === 'EngagedVisitorDwell'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :cluster="chosenCluster"
        />

        <!-- Visit Dwell -->
        <VisitDwell
          v-else-if="widget.code_name === 'EngagedVisitDwell'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :day="chosenDay"
        />

        <!-- Distributed  Dwell -->
        <DistributedDwell
          v-else-if="widget.code_name === 'DistributedDwell'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :day="chosenDay"
        />

        <!-- Visitors by Day -->
        <VisitorDay
          v-else-if="widget.code_name === 'VisitorsByDay'"
          :widget="widget"
          :area="chosenArea"
          :time="chosenTime"
          :zone="chosenZone"
          :day="chosenDay"
        />

        <!-- Benchmark Engaged Repeat Visits -->
        <BenchmarkEngagedRepeatVisits
          v-else-if="widget.code_name === 'BenchmarkEngagedRepeatVisits'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <!-- Benchmark Conversion Rate -->
        <BenchmarkConversionRate
          v-else-if="widget.code_name === 'BenchmarkConversionRate'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <!-- Benchmark Engaged Dwell Time -->
        <BenchmarkEngagedDwellTime
          v-else-if="widget.code_name === 'BenchmarkEngagedDwellTime'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <!-- Premium Widgets -->
        <TotalEngagedDwellByZone
          v-else-if="widget.code_name === 'PremiumTotalEngagedDwellByZone'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <CumulativeArrival
          v-else-if="widget.code_name === 'PremiumCumulativeArrival'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <ZoneOverlap
          v-else-if="widget.code_name === 'PremiumZoneOverlap'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <MostCommonPath
          v-else-if="widget.code_name === 'PremiumMostCommonPath'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <ArrivalsByDay
          v-else-if="widget.code_name === 'PremiumArrivalsByDay'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
        />

        <ZoneCountVisited
          v-else-if="widget.code_name === 'PremiumZonesVisited'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
          :time="chosenTime"
          :day="chosenDay"
        />

        <FirstZoneVisited
          v-else-if="widget.code_name === 'PremiumFirstZoneVisited'"
          :widget="widget"
          :area="chosenArea"
          :zone="chosenZone"
          :time="chosenTime"
        />
      </div>
    </div>

    <div class="widget__footer">
      <div class="widget__footer-controls" v-show="editMode">
        <div class="widget-edit-controls">
          <span class="widget-control widget-control--move" aria-label="Move widget">
            <IconMove />
          </span>
          <span class="widget-control widget-control--remove">
            <Button severity="tertiary" @click="removeWidget" aria-label="Remove widget">
              <template #iconLeft><IconRemove /></template>
            </Button>
          </span>
          <span class="widget-control widget-control--resize" aria-label="Resize widget">
            <!-- Functionality handled by plugin -->
            <IconResize />
          </span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import mixpanel from '@/mixpanel.ts';

import Button from '../global/Button.vue';
import InputSelect from '../inputs/InputSelect.vue';
import Tooltip from '../global/Tooltip.vue';

import type { WidgetFilter, WidgetSetting } from '@/types/Widget';

import ArrivalsByDay from './premium-metrics/ArrivalsByDay.vue';
import AveragesDisplay from './averages/AveragesDisplay.vue';
import BenchmarkEngagedRepeatVisits from './benchmarking/EngagedRepeatVisits.vue';
import BenchmarkConversionRate from './benchmarking/ConversionRate.vue';
import BenchmarkEngagedDwellTime from './benchmarking/EngagedDwellTime.vue';
import CumulativeArrival from './premium-metrics/CumulativeArrival.vue';
import DaysVisited from './days-visited/DaysVisited.vue';
import DistributedDwell from './distributed-dwell/DistributedDwell.vue';
import TotalEngagedDwellByZone from './premium-metrics/TotalEngagedDwellByZone.vue';
import MostCommonPath from './premium-metrics/MostCommonPath.vue';
import RepeatVisits from './repeat-visits/RepeatVisits.vue';
import RepeatVisitors from './repeat-visitors/RepeatVisitors.vue';
import UniqueVisitors from './unique-visitors/UniqueVisitors.vue';
import Visits from './visits/Visits.vue';
import VisitDwell from './visit-dwell/VisitDwell.vue';
import VisitorDay from './visitor-day/VisitorDay.vue';
import VisitorDwell from './visitor-dwell/VisitorDwell.vue';
import ZoneOverlap from './premium-metrics/ZoneOverlap.vue';
import ZonesVisited from './zones-visited/ZonesVisited.vue';
import ZoneCountVisited from './premium-metrics/ZoneCountVisited.vue';
import FirstZoneVisited from './premium-metrics/FirstZoneVisited.vue';

import IconBar from '@/icons/analytics/IconBar.svg?component';
import IconInfo from '@/icons/IconInfo.svg?component';
import IconMove from '@/icons/widgets/IconMove.svg?component';
import IconRemove from '@/icons/IconRemove.svg?component';
import IconResize from '@/icons/widgets/IconResize.svg?component';
import IconPremium from '@/icons/IconPremium.svg?component';
import FormCell from '../forms/FormCell.vue';
import FormRow from '../forms/FormRow.vue';

const widget = defineModel<any>('widget');

defineProps({
  editMode: Boolean,
});

const emit = defineEmits(['onWidgetRemove']);

const chosenArea = ref<WidgetFilter>();
const timeIndex = ref<number>();
const chosenZone = ref<WidgetSetting>();
const chosenDay = ref<WidgetSetting>();
const chosenCluster = ref<WidgetSetting>();

// Set Defaults
if (widget.value && widget.value.filters) {
  if (widget.value.filters.area) {
    chosenArea.value = widget.value.filters.area[0];

    const zoneFilter = widget.value.filters.area.find((area: WidgetFilter) => {
      return area.name == 'Zone';
    });

    if (zoneFilter && zoneFilter.data) {
      chosenZone.value = zoneFilter.data[0];
    }

    const clusterFilter = widget.value.filters.area.find((area: WidgetFilter) => {
      return area.name == 'Cluster';
    });

    if (clusterFilter && clusterFilter.data) {
      chosenCluster.value = clusterFilter.data[0];
    }
  }

  if (widget.value.filters.time) {
    timeIndex.value = 0;

    const dailyFilter = widget.value.filters.time.find((time: WidgetFilter) => {
      return ['Day', 'Hour', '15 Min'].includes(time.name);
    });

    if (dailyFilter && dailyFilter.data) {
      chosenDay.value = dailyFilter.data[0];
    }
  }
}

const changeArea = (area: WidgetFilter) => {
  chosenArea.value = area;
  mixpanel.track('Widget Interaction', {
    Widget: widget.value?.code_name,
    Action: 'Filter Change',
    'Filter Type': 'Area',
  });
};

const chosenTime = computed(() => {
  if (!widget.value.filters.time) {
    return null;
  }
  return widget.value.filters.time[timeIndex.value];
});

const showDaySelection = computed(() => {
  if (!chosenTime.value) {
    return false;
  }
  const isDaily = chosenTime.value?.name === 'Day';

  if (isDaily) {
    const isDailyZone = chosenArea.value?.name === 'Zone Average';
    const isVisitorDistribution = widget.value?.code_name === 'VisitorsByDay';
    const isDistributedDwell = widget.value?.code_name === 'DistributedDwell';
    const isZoneCountVisited = widget.value?.code_name === 'PremiumZonesVisited';

    return isDailyZone || isVisitorDistribution || isDistributedDwell || isZoneCountVisited;
  }

  const isUnique = widget.value?.code_name === 'UniqueVisitors';
  return isUnique && chosenTime.value?.name != 'Total';
});

const showZoneSelection = computed(() => {
  const isZoneArea = chosenArea.value?.name === 'Zone';
  const isTotal = chosenTime.value?.name === 'Total';

  if (widget.value?.code_name === 'UniqueVisitors') {
    return isZoneArea && !isTotal;
  }

  return isZoneArea;
});

const showClusterSelection = computed(() => {
  const isClusterArea = chosenArea.value?.name === 'Cluster';
  const isTotal = chosenTime.value?.name === 'Total';

  if (widget.value?.code_name === 'UniqueVisitors') {
    return isClusterArea && !isTotal;
  }

  return isClusterArea;
});

watch(chosenZone, (newZone) => {
  if (newZone) {
    mixpanel.track('Widget Interaction', {
      widget: widget.value?.code_name,
      action: 'Filter Change',
      filterType: 'Zone',
    });
  }
});

watch(chosenCluster, (newCluster) => {
  if (newCluster) {
    mixpanel.track('Widget Interaction', {
      widget: widget.value?.code_name,
      action: 'Filter Change',
      filterType: 'Cluster',
    });
  }
});

watch (chosenTime, (time) => {
  if (time) {
    const day = chosenDay.value?.id;
    if (day && chosenTime.value) {
      const equalDay = chosenTime.value.data?.find((time) => {
        return time.id === day;
      });

      chosenDay.value = equalDay ? equalDay : time.data ? time.data[0] : undefined;
    } else {
      chosenDay.value = time.data ? time.data[0] : undefined;
    }
    mixpanel.track('Widget Interaction', {
      Widget: widget.value?.code_name,
      Action: 'Filter Change',
      'Filter Type': 'Time',
    });
  }
});

watch(chosenDay, (newDay) => {
  if (newDay) {
    mixpanel.track('Widget Interaction', {
      widget: widget.value?.code_name,
      action: 'Filter Change',
      filterType: 'Day',
    });
  }
});

const removeWidget = () => {
  emit('onWidgetRemove', widget.value?.id);
};

const changeZone = (zoneId: string) => {
  const newZone = chosenArea.value?.data?.find(zone => zone.id === zoneId);
  chosenZone.value = newZone;
};

const changeCluster = (clusterId: string) => {
  const newCluster = chosenArea.value?.data?.find(cluster => cluster.id === clusterId);
  chosenCluster.value = newCluster;
};

const changeDay = (dayId: string) => {
  const newDay = chosenTime.value?.data?.find(day => day.id === dayId);
  chosenDay.value = newDay;
};

</script>

<style lang="scss">
.widget {
  @include sizing.padding('xxs');
  background: variables.$primitives-color-background-100;
  border-radius: variables.$sizing-xs;
  position: relative;

  &.premium-metric {
    border: 1px solid variables.$primitives-color-secondary-400;
  }

  .widget__header {
    @include mixins.flex-center($horiz: flex-start, $wrap: true);
    border-bottom: 1px solid variables.$primitives-color-border-200;
    margin-bottom: variables.$sizing-xs;
    padding-bottom: variables.$sizing-xs;

    span {
      @include sizing.margin(0, 'xxxs');
    }
  }

  .widget__subheader {
    @include mixins.flex-center($horiz: flex-start, $gap: variables.$sizing-xs, $wrap: true);
    margin-bottom: variables.$sizing-xs;
  }

  .widget__header-text {
    @include sizing.padding('xs');
    @include typography.font-body($size: sm, $weight: normal);
    text-align: center;

    span:not(.widget-message__yellow) {
      font-weight: bold;
    }
  }

  .widget__bignumber {
    @include typography.font-display($size: lg, $weight: bold);
    color: variables.$primitives-color-tertiary-500;
    line-height: variables.$sizing-xxxl;
    text-align: center;
  }

  .widget__label {
    @include mixins.flex-center();
    @include sizing.padding(0, 'xl');
    @include typography.font-body($size: sm, $weight: bold);
    color: variables.$primitives-color-tertiary-400;
    text-align: center;
    text-transform: uppercase;

    &.widget__label-thin {
      @include typography.font-body($size: sm, $weight: medium);
      display: block;

      span:not(.widget-message__yellow) {
        font-weight: bold;
      }
    }
  }
}

.widget__body-wrapper {
  align-items: center;
  display: flex;
  height: calc(100% - 130px);
  margin: auto 0;
}

.widget__body {
  height: 100%;
  width: 100%;
}

.widget__body-empty {
  @include mixins.flex-center();
  flex-direction: column;
  @include typography.font-body($size: sm, $weight: normal);
  color: variables.$primitives-color-tertiary-400;
  height: 100%;
  text-align: center;
  width: 100%;
}

.widget__header-controls {
  @include mixins.flex-center($horiz: flex-start);
  list-style-type: none;
  margin-left: auto;
  padding: 0;

  @media screen and (max-width: 768px) {
    margin-top: variables.$sizing-xs;
    width: 100%;
  }

  &.widget__header-controls__zones {
    margin-left: 0;
  }

  li {
    @include sizing.padding('xxxs', 'xs');
    background: variables.$primitives-color-background-100;
    border: 1px solid variables.$primitives-color-primary-500;
    color: variables.$primitives-color-primary-500;
    cursor: pointer;
  }

  li:first-of-type {
    border-bottom-left-radius: variables.$sizing-xxs;
    border-top-left-radius: variables.$sizing-xxs;
  }

  li:last-of-type {
    border-bottom-right-radius: variables.$sizing-xxs;
    border-top-right-radius: variables.$sizing-xxs;
  }

  .widget__header-control--active {
    background-color: variables.$primitives-color-primary-500;
    color: variables.$primitives-color-background-100;
  }

  .widget__header-control--solo {
    background-color: transparent;
    border-color: transparent;
    color: variables.$primitives-color-primary-500;
  }

  .widget__label {
    padding: 0;
  }
}

.widget__footer {
  bottom: 0;
  position: absolute;
  right: 0;
}

.widget__footer-controls {
  bottom: -4px;
  position: absolute;
  right: -8px;
}

.widget__subheader-controls {
  @include mixins.flex-center($horiz: flex-start, $gap: variables.$sizing-xxs);
  list-style-type: none;
  margin-left: auto;
  padding: 0;

  @media screen and (max-width: 768px) {
    margin-left: 0;
  }

  &.widget__subheader-controls__zones {
    flex: 1 1 auto;
    margin-left: 0;
    max-width: 100%;
  }

  &.widget__subheader-controls__days {
    flex: 0 0 auto;
  }

  li {
    @include sizing.padding('xxxs', 'xs');
    background: variables.$primitives-color-background-200;
    border: 1px solid variables.$primitives-color-border-100;
    border-radius: 2px;
    cursor: pointer;
  }

  .widget__header-control--active {
    background-color: variables.$primitives-color-blue-100;
    border-color: variables.$primitives-color-blue-200;
    color: variables.$primitives-color-tertiary-700;
  }

  .widget__label {
    padding: 0;
  }
}

.dwell-time__icon {
  @include mixins.flex-center();
  border: 1px solid variables.$primitives-color-border-200;
  border-radius: variables.$sizing-xxxs;
  height: 28px;
  margin: 0 auto;
  width: 28px;
}

.widget-message__yellow {
  background: variables.$primitives-color-secondary-100;
  border: 1px solid variables.$primitives-color-secondary-400;
  border-radius: variables.$sizing-xxxs;
  display: inline-block;
  font-weight: normal;
  margin-top: variables.$sizing-xxxs;
  padding: 2px variables.$sizing-xxs;
}

.widget p {
  max-width: 250px;
  text-align: center;
}

.dwell-time__container {
  @include sizing.padding('lg');
  background-color: variables.$primitives-color-border-100;
  border-radius: variables.$sizing-xxs;
  width: 100%;
}

.widget .dwell-time__time {
  @include mixins.flex-center($gap: variables.$sizing-xs);
  width: 100%;

  .widget__bignumber {
    @include typography.font-display($size: md, $weight: bold);
    flex-shrink: 0;
  }

  .widget__bignumber.divider {
    align-self: flex-start;
    line-height: variables.$sizing-xxl;
    width: variables.$sizing-xxs;
  }

  .widget__label {
    @include typography.font-body($size: xs, $weight: medium);
    padding: 0;
  }
}

.dwell__time-text {
  @include sizing.padding('xs');
  @include typography.font-body($size: sm, $weight: normal);
  text-align: center;
}

.ui-resizable-handle {
  background: none;
  opacity: 0;

  &::before,
  &::after {
    @include sizing.padding('xxxs');
    content: '';
    display: block;
    height: 24px;
    position: absolute;
    right: -4px;
    top: 4px;
    width: 24px;
  }

  &::before {
    background-color: variables.$primitives-color-tertiary-100;
    border: 1px solid variables.$primitives-color-tertiary-200;
    border-radius: 4px;
    transform: rotate(45deg);
    z-index: 0;
  }

  &::after {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="%23666" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 20 20"><path d="m10 3 2 2H8l2-2v14l-2-2h4l-2 2"/></svg>');
    transform: rotate(-180deg);
  }
}

.widgets--edit .ui-resizable-handle {
  opacity: 1;
}

.grid-stack-item > .ui-resizable-se {
  // Important to overwrite the plugin doing these by ID
  bottom: 12px !important;
  height: 38px;
  opacity: 0;
  right: 10px !important;
  width: 38px;
}

.widget-edit-controls {
  @include mixins.flex-center($gap: variables.$sizing-xxxs);
  background: variables.$primitives-color-background-200;
  border: 1px solid variables.$primitives-color-tertiary-200;
  border-radius: 4px;
  color: variables.$primitives-color-tertiary-500;
  margin-right: 4px;
  padding: 4px;

  .widget-control {
    @include mixins.flex-center($gap: variables.$sizing-xxs);
    @include sizing.padding('xxs');
    @include mixins.rounded-border(
      $color: variables.$primitives-color-tertiary-200,
      $size: variables.$sizing-xxxs
    );
    margin: 0;
    position: relative;

    svg {
      margin: 0;
    }
  }

  .widget-control--move {
    cursor: move;
  }

  .widget-control--remove {
    padding: 0;
  }

  .widget-move,
  .widget-remove,
  .widget-resize {
    height: 38px;
    width: 38px;
  }
}

.apexcharts-toolbar .apexcharts-menu-icon {
  background: url('/chart-download.png') no-repeat center center;
  background-size: 20px;

  svg {
    display: none;
  }
}

.grid-stack > .grid-stack-item > .grid-stack-item-content {
  overflow: visible;

  @media screen and (max-width: 768px) {
    background-color: variables.$primitives-color-background-100;
    border: 1px solid variables.$primitives-color-border-200;
    border-radius: variables.$sizing-xxxs;
    left: 0 !important;
    min-height: 100%;
    position: relative !important;
    top: 0 !important;
    width: 100% !important;
    /* NOTE: !important is required to override the grid-stack plugin. all of the widgets on mobile are the same size, regardless of the classes added to them */
  }

  .widget__body {
    overflow-x: auto;
    overflow-y: auto;
  }
}

.chart-wrapper {
  @include sizing.padding('sm', 0);
  height: calc(100% - 12px);
  position: relative;
  // -1px needed to stop the overflow scrollbars from showing (do not add overflow: hidden or the tooltips will be cut off)
  width: calc(100% - 1px);
}

.unique-total .chart-wrapper {
  height: calc(100% - 80px);
}

.widget__header-title {
  @include mixins.flex-center();
  margin-right: variables.$sizing-xxxs;

  .premium-icon {
    fill: variables.$primitives-color-secondary-500;
  }
}

.widget__content-center {
  height: 100%;

  &.widget__content-center--flex {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: variables.$sizing-xxs;
    height: 100%;
    justify-content: center;
    width: 100%;
  }
}

.apexcharts-toolbar:hover .apexcharts-menu {
  // Stolen from the .apexcharts-menu-open class that is normally applied on click
  opacity: 1;
  pointer-events: all;
  transition: 0.15s ease all;
}
</style>
